import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CtaButton } from "@/components/ui/CtaButton";
import { GlowField } from "@/components/ui/GlowField";
import { Plus, Users, GraduationCap, Building2, CalendarClock, ReceiptText, Trash2, X, Mail, BookOpen, Search } from "lucide-react";

export default function InstituteDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [billing, setBilling] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showInvite, setShowInvite] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s, c, b] = await Promise.all([
        api.get("/institute/dashboard"),
        api.get("/institute/students"),
        api.get("/institute/classes"),
        api.get("/institute/billing"),
      ]);
      setData(d.data);
      setStudents(s.data.students || []);
      setClasses(c.data.classes || []);
      setBilling(b.data);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeTeacher = async (id) => {
    if (!window.confirm("Remove this teacher from your institute?")) return;
    try { await api.delete(`/institute/teachers/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [teacherSearch, setTeacherSearch] = useState("");
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const toggleTeacher = (id) => setSelectedTeachers((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const bulkRemoveTeachers = async (list) => {
    if (!selectedTeachers.length) return;
    if (!window.confirm(`Remove ${selectedTeachers.length} teacher${selectedTeachers.length > 1 ? "s" : ""} from your institute?`)) return;
    const res = await Promise.allSettled(selectedTeachers.map((id) => api.delete(`/institute/teachers/${id}`)));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    if (ok) toast.success(`Removed ${ok} teacher${ok > 1 ? "s" : ""}`);
    if (ok < selectedTeachers.length) toast.error(`${selectedTeachers.length - ok} could not be removed`);
    setSelectedTeachers([]);
    load();
  };

  if (loading) return <div className="text-sm text-neutral-500">Loading…</div>;
  const stats = data?.stats || {};
  const teachers = data?.teachers || [];

  return (
    <div className="space-y-8" data-testid="institute-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1 inline-flex items-center gap-2">
            <Building2 size={12} /> / institute · {data?.institute_name}
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Hello, {user?.full_name?.split(" ")[0]}.</h1>
          <p className="text-sm text-neutral-600 mt-1">Run your coaching centre, one teacher and one student at a time.</p>
        </div>
        <CtaButton onClick={() => setShowInvite(true)} variant="primary" size="md" className="rounded-md" data-testid="invite-teacher-btn">
          <Plus size={16} /> Invite teacher
        </CtaButton>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Teachers" value={stats.teacher_count || 0} />
        <Stat icon={GraduationCap} label="Students" value={stats.student_count || 0} />
        <Stat icon={CalendarClock} label="Upcoming classes" value={stats.upcoming_classes || 0} />
        <Stat icon={ReceiptText} label="Pending fees" value={`$${(stats.pending_fees_total || 0).toFixed(0)}`} />
      </div>

      <div className="inline-flex border border-black/10 bg-white rounded-full p-1">
        {["overview", "teachers", "students", "classes", "billing"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`inst-tab-${t}`}
            className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] font-semibold rounded-full transition-colors ${tab === t ? "bg-[#0A0C10] text-[#FBFAF7]" : "text-neutral-500 hover:text-[#0A0C10]"}`}
          >{t}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title={`Teachers (${teachers.length})`} cta={<button onClick={() => setTab("teachers")} className="text-xs text-neutral-500 hover:text-[#0A0C10] underline">All</button>}>
            {teachers.slice(0, 5).map((t) => (
              <Row key={t.id}>
                <div>
                  <div className="font-medium text-sm">{t.full_name}</div>
                  <div className="text-xs text-neutral-500">{t.email} · {(t.subjects || []).slice(0, 2).join(", ") || "—"}</div>
                </div>
                <div className="text-xs text-neutral-500">{t.ai_credits} credits</div>
              </Row>
            ))}
            {teachers.length === 0 && <Empty msg="No teachers yet — invite one." />}
          </Section>
          <Section title={`Recent classes (${classes.length})`} cta={<button onClick={() => setTab("classes")} className="text-xs text-neutral-500 hover:text-[#0A0C10] underline">All</button>}>
            {classes.slice(0, 5).map((c) => (
              <Row key={c.id}>
                <div>
                  <div className="font-medium text-sm">{c.title}</div>
                  <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)}</div>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${c.status === "live" ? "bg-green-100 text-green-700" : c.status === "completed" ? "bg-neutral-100 text-neutral-600" : "bg-[#FFB400]/15 text-[#0A0C10]"}`}>{c.status}</span>
              </Row>
            ))}
            {classes.length === 0 && <Empty msg="No classes yet." />}
          </Section>
        </div>
      )}

      {tab === "teachers" && (
        <Section title="All teachers" wide>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                value={teacherSearch} onChange={(e) => setTeacherSearch(e.target.value)}
                placeholder="Search teachers…" data-testid="institute-teachers-search"
                className="pl-8 pr-3 py-1.5 rounded-md border border-neutral-200 focus:border-[#0A0C10] outline-none text-xs w-56"
              />
            </div>
            {selectedTeachers.length > 0 && (
              <button onClick={bulkRemoveTeachers} data-testid="institute-teachers-bulk-delete"
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md">
                <Trash2 size={12} /> Remove selected ({selectedTeachers.length})
              </button>
            )}
          </div>
          {teachers.length === 0 && <Empty msg="No teachers yet — invite one." />}
          {teachers
            .filter((t) => !teacherSearch.trim() || `${t.full_name} ${t.email} ${(t.subjects || []).join(" ")}`.toLowerCase().includes(teacherSearch.trim().toLowerCase()))
            .map((t) => (
            <Row key={t.id}>
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={selectedTeachers.includes(t.id)} onChange={() => toggleTeacher(t.id)} data-testid={`institute-teacher-check-${t.id}`} />
                <div>
                  <div className="font-medium text-sm">{t.full_name}</div>
                  <div className="text-xs text-neutral-500">{t.email} · {t.experience_years || 0}y exp · {t.teaching_language}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{(t.subjects || []).join(", ") || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-neutral-500">{t.ai_credits} credits</span>
                <button onClick={() => removeTeacher(t.id)} data-testid={`remove-teacher-${t.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
              </div>
            </Row>
          ))}
        </Section>
      )}

      {tab === "students" && (
        <Section title={`All students (${students.length})`} wide>
          {students.length === 0 && <Empty msg="No students yet." />}
          {students.map((s) => (
            <Row key={s.id}>
              <div>
                <div className="font-medium text-sm">{s.full_name}</div>
                <div className="text-xs text-neutral-500"><GraduationCap size={11} className="inline" /> {s.grade || "—"} · {s.email}</div>
              </div>
              <div className="text-xs text-neutral-500">Teacher: <span className="text-[#0A0C10]">{s.teacher_name || "—"}</span></div>
            </Row>
          ))}
        </Section>
      )}

      {tab === "classes" && (
        <Section title={`All classes (${classes.length})`} wide>
          {classes.length === 0 && <Empty msg="No classes yet." />}
          {classes.map((c) => (
            <Row key={c.id}>
              <div>
                <div className="font-medium text-sm inline-flex items-center gap-2"><BookOpen size={12} /> {c.title}</div>
                <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)}</div>
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${c.status === "live" ? "bg-green-100 text-green-700" : c.status === "completed" ? "bg-neutral-100 text-neutral-600" : "bg-[#FFB400]/15 text-[#0A0C10]"}`}>{c.status}</span>
            </Row>
          ))}
        </Section>
      )}

      {tab === "billing" && (
        <Section title="Central billing" wide>
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <Stat icon={ReceiptText} label="Plan" value={billing?.plan || "—"} />
            <Stat icon={Users} label="Active teachers" value={billing?.teachers?.length || 0} />
            <Stat icon={Mail} label="AI credits (pool)" value={billing?.total_credits_remaining ?? "—"} />
          </div>
          {(billing?.teachers || []).map((t) => (
            <Row key={t.id}>
              <div>
                <div className="font-medium text-sm">{t.full_name}</div>
                <div className="text-xs text-neutral-500">{t.email} · {t.plan}</div>
              </div>
              <div className="text-xs text-neutral-500">{t.ai_credits} credits · {(t.storage_used_mb || 0).toFixed(1)} MB</div>
            </Row>
          ))}
        </Section>
      )}

      {showInvite && <InviteTeacherModal onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">{label}</div>
        <Icon size={16} className="text-neutral-400" />
      </div>
      <div className="font-display font-bold text-3xl mt-2">{value}</div>
    </div>
  );
}
function Section({ title, children, cta, wide }) {
  return (
    <div className={`bg-white border border-black/8 rounded-2xl p-6 ${wide ? "lg:col-span-2" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold">{title}</div>
        {cta}
      </div>
      {children}
    </div>
  );
}
function Row({ children }) { return <div className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0 gap-3">{children}</div>; }
function Empty({ msg }) { return <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200 rounded-lg">{msg}</div>; }

function InviteTeacherModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/institute/teachers/invite", { full_name: name, email });
      toast.success(data.linked_existing ? "Existing teacher linked" : "Invitation email sent");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white w-full max-w-md p-6 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ invite teacher</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">Add a teacher</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3 mt-3">
          <GlowField label="Full name" required value={name} onChange={(e) => setName(e.target.value)} testid="invite-name" />
          <GlowField label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="invite-email" />
          <p className="text-xs text-neutral-500">We'll email them an invitation with a temporary password. If they're already a teacher on TeachingSoftware.ai, we'll just link their account.</p>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <CtaButton type="button" variant="ghost" onClick={onClose} className="rounded-full">Cancel</CtaButton>
          <CtaButton type="submit" variant="primary" disabled={loading} className="rounded-full" data-testid="invite-submit">{loading ? "Sending…" : "Send invite"}</CtaButton>
        </div>
      </form>
    </div>
  );
}
